import { useApolloClient, useMutation, useQuery } from "@apollo/client";
import gql from "graphql-tag";
import Cookies from "js-cookie";
import React from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { toastSuccessOptions } from "../../../App";
import { knownLanguages } from "../../../knownLanguages";
import { formatPasswordChangeDate } from "../../../utils/formatPasswordChangedWhen";
import { handleGraphQLErrors } from "../../../utils/handleGraphQLError";
import { Button } from "../../SharedComponents/Button/Button";
import { EditableInformation } from "../../SharedComponents/EditableInformation/EditableInformation";
import { EditableInformationPassword } from "../../SharedComponents/EditableInformation/EditableInformationPassword/EditableInformationPassword";
import { EditableInformationStatus } from "../../SharedComponents/EditableInformation/EditableInformationStatus/EditableInformationStatus";
import { EditableInformationText } from "../../SharedComponents/EditableInformation/EditableInformationText/EditableInformationText";
import { ListLoadingItemSingleBar } from "../../SharedComponents/ListLoading/ListLoading";
import { ProfileHeader } from "../../SharedComponents/ProfileHeader/ProfileHeader";
import { OnUserUpdatedSubscription } from "../../Subscriptions/onSSOUserUpdated/onSSOUserUpdated";
import "./AdminProfile.css";

export const AdminProfile = () => {
  const { t } = useTranslation();
  const { data, error, loading } = useQuery(getUserInformationQuery);
  const [userSignOut] = useMutation(signOutMutation);
  const history = useHistory();
  const client = useApolloClient();
  const [updateUserInformation] = useMutation(
    updateUserProfileInformationMutation
  );

  const signOut = () => {
    userSignOut()
      .catch((err) => {
        console.error("Error", err);
      })
      .finally(async () => {
        await client.clearStore();
        Cookies.remove("access_level");
        history.push("/login");
      });
  };

  const updateUserInformationHandler = (updatedInfo) => {
    updateUserInformation({
      variables: {
        id: data?.user?.id,
        ...updatedInfo,
        updateToken: true,
      },
    })
      .then(() => toast(t("toasts.userInfoUpdated"), toastSuccessOptions))
      .catch((err) => handleGraphQLErrors(err));
  };

  return (
    <div className="AdminProfile">
      {error && <div>{error.message}</div>}
      <OnUserUpdatedSubscription userId={data?.user?.id} />
      <div className="AdminProfile__background"></div>
      <div className="AdminProfile__content">
        <div className="AdminProfile__header">
          <ProfileHeader name={data?.user?.name || " "} />
        </div>
        <div className="AdminProfile__personalinfo">
          <EditableInformation title="sectionTitles.profile">
            {loading && (
              <>
                <ListLoadingItemSingleBar />
                <ListLoadingItemSingleBar />
                <ListLoadingItemSingleBar />
              </>
            )}
            {!error && !loading && (
              <>
                <EditableInformationText
                  label={t("generic.name")}
                  value={data?.user?.name}
                  onSubmit={updateUserInformationHandler}
                  validationSchema={nameValidationSchema}
                  name="name"
                />
                <EditableInformationText
                  label={t("generic.email")}
                  value={data?.user?.email}
                  onSubmit={updateUserInformationHandler}
                  validationSchema={emailValidationSchema}
                  name="email"
                />
                <EditableInformationStatus
                  label={t("generic.language")}
                  value={
                    knownLanguages.find(
                      (lang) => lang?.value === data?.user?.prefferedLanguage
                    )?.value
                  }
                  onSubmit={updateUserInformationHandler}
                  validationSchema={languageValidationSchema}
                  name="prefferedLanguage"
                  options={knownLanguages}
                  withSquare={false}
                  isLang={true}
                />
                <EditableInformationPassword
                  label={t("generic.password")}
                  value={data?.user?.password}
                  passwordChangedWhen={
                    data?.user?.passwordChangedAt
                      ? formatPasswordChangeDate(data?.user?.passwordChangedAt)
                      : formatPasswordChangeDate(data?.user?.createdAt)
                  }
                />
              </>
            )}
          </EditableInformation>
        </div>
      </div>
      <div className="AdminProfile__right">
        <div className="AdminProfile__right--displacer"></div>
        <div className="AdminProfile__right--actions">
          <div className="AdminProfile__right--action">
            <Button buttonStyle="Button__signout" click={() => signOut()}>
              {t("profile.signOut")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const getUserInformationQuery = gql`
  query {
    user {
      id
      email
      name
      passwordChangedAt
      createdAt
      prefferedLanguage
    }
  }
`;

const signOutMutation = gql`
  mutation signOutMutation {
    userLogout
  }
`;

const updateUserProfileInformationMutation = gql`
  mutation updateUser(
    $id: ID!
    $email: String
    $name: String
    $prefferedLanguage: String
    $updateToken: Boolean
  ) {
    updateSSOUser(
      id: $id
      email: $email
      name: $name
      prefferedLanguage: $prefferedLanguage
      updateToken: $updateToken
    ) {
      id
      name
      email
      prefferedLanguage
      passwordChangedAt
    }
  }
`;

const nameValidationSchema = Yup.object().shape({
  name: Yup.string().required("You must enter a name"),
});

const emailValidationSchema = Yup.object().shape({
  email: Yup.string().email().required("You must enter a e-mail"),
});

const languageValidationSchema = Yup.object().shape({
  prefferedLanguage: Yup.string(),
});
